import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { MagnifyingGlassIcon, EnvelopeIcon, PhoneIcon, TruckIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';

function GuestOrderTrackingPage() {
  const [searchData, setSearchData] = useState({
    email: '',
    orderNumber: ''
  });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchData.email || !searchData.orderNumber) {
      toast.error('Lütfen email adresinizi ve sipariş numaranızı girin.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/orders/guest-tracking', {
        email: searchData.email,
        orderNumber: searchData.orderNumber
      });
      
      setOrder(response);
      toast.success('Sipariş bulundu!');
    } catch (error) {
      console.error('Sipariş arama hatası:', error);
      toast.error('Sipariş bulunamadı. Email adresinizi ve sipariş numaranızı kontrol edin.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Hazırlanıyor',
      'processing': 'İşleniyor',
      'confirmed': 'Onaylandı',
      'shipped': 'Kargoya Verildi',
      'delivered': 'Teslim Edildi',
      'cancelled': 'İptal Edildi',
      'shipping_failed': 'Kargo Hatası',
      'delivery_failed': 'Teslim Başarısız',
      'cancellation_requested': 'İptal Talebi',
      'lost': 'Kayıp',
      'return_requested': 'İade Talep Edildi'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'confirmed': 'bg-green-100 text-green-800',
      'shipped': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-emerald-100 text-emerald-800',
      'cancelled': 'bg-red-100 text-red-800',
      'shipping_failed': 'bg-red-200 text-red-900',
      'delivery_failed': 'bg-orange-200 text-orange-900',
      'cancellation_requested': 'bg-yellow-200 text-yellow-900',
      'lost': 'bg-gray-200 text-gray-900',
      'return_requested': 'bg-orange-100 text-orange-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Misafir Sipariş Takibi
          </h1>
          <p className="text-lg text-gray-600">
            Email adresiniz ve sipariş numaranız ile siparişinizi takip edebilirsiniz
          </p>
        </div>

        {/* Arama Formu */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <EnvelopeIcon className="w-5 h-5 inline mr-2" />
                  Email Adresiniz
                </label>
                <input
                  type="email"
                  value={searchData.email}
                  onChange={(e) => setSearchData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="ornek@email.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <TruckIcon className="w-5 h-5 inline mr-2" />
                  Sipariş Numarası
                </label>
                <input
                  type="text"
                  value={searchData.orderNumber}
                  onChange={(e) => setSearchData(prev => ({ ...prev, orderNumber: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent"
                  placeholder="MINELA-MNL-000001"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-4 px-8 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Aranıyor...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                  Siparişi Ara
                </div>
              )}
            </button>
          </form>
        </div>

        {/* Sipariş Detayları */}
        {order && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="border-b border-gray-200 pb-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Sipariş #{order.order_number}
                  </h2>
                  <p className="text-gray-600">
                    Sipariş Tarihi: {new Date(order.created_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>
                <div className="mt-4 md:mt-0">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Müşteri Bilgileri */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Müşteri Bilgileri</h3>
                <div className="space-y-2">
                  <p className="flex items-center text-gray-600">
                    <span className="font-medium w-20">Ad Soyad:</span>
                    {order.guest_name}
                  </p>
                  <p className="flex items-center text-gray-600">
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    <span className="font-medium w-16">Email:</span>
                    {order.guest_email}
                  </p>
                  <p className="flex items-center text-gray-600">
                    <PhoneIcon className="w-4 h-4 mr-2" />
                    <span className="font-medium w-16">Telefon:</span>
                    {order.guest_phone}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Teslimat Adresi</h3>
                <div className="text-gray-600">
                  <p className="font-medium">{order.shipping_address?.full_name}</p>
                  <p>{order.shipping_address?.address_line}</p>
                  <p>{order.shipping_address?.district} / {order.shipping_address?.city}</p>
                  {order.shipping_address?.postal_code && (
                    <p>Posta Kodu: {order.shipping_address.postal_code}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Kargo Bilgileri */}
            {order.tracking_number && (
              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kargo Bilgileri</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Kargo Firması</p>
                    <p className="font-medium">{order.shipping_company}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Takip Numarası</p>
                    <p className="font-medium">{order.tracking_number}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Sipariş Ürünleri */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Sipariş Ürünleri</h3>
              <div className="space-y-4">
                {order.order_items?.map((item, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                    {item.products?.main_image_url && (
                      <img
                        src={item.products.main_image_url}
                        alt={item.product_name_at_purchase}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{item.product_name_at_purchase}</h4>
                      <p className="text-sm text-gray-600">
                        {item.variant_details_at_purchase?.color} - {item.variant_details_at_purchase?.size}
                      </p>
                      <p className="text-sm text-gray-600">Adet: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {(item.price_at_purchase * item.quantity).toFixed(2)} ₺
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Toplam Tutar:</span>
                  <span className="text-2xl font-bold text-brand-green">
                    {parseFloat(order.total_price).toFixed(2)} ₺
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GuestOrderTrackingPage;